import RtaEngineeringPanel from './RtaEngineeringPanel';

import SensitivityForecastPanel from './SensitivityForecastPanel';

import RtaPseudopressurePanel from './RtaPseudopressurePanel';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useProductionForecastStore,
} from '../../hooks/useProductionForecastStore';

import {
  ETH,
} from '../economics/ecoShared';

import {
  buildRtaPoints,
  fitRtaLinearTrend,
} from './rtaCalculations';

import type {
  RtaLinearFit,
  RtaPoint,
  RtaRawPoint,
} from './rtaCalculations';

export default function RtaAnalysisPanel({
  projectId,
}: {
  projectId: number;
}) {
  const project =
    useProductionForecastStore(
      (state) =>
        state.projects[
          String(projectId)
        ],
    );

  const [
    reservoirPressureText,
    setReservoirPressureText,
  ] = useState('');

  const productionRows =
    project?.productionRows ??
    [];

  const detectedReservoirPressure =
    useMemo(() => {
      return findNumberRecursive(
        project,
        [
          'reservoirPressure',
          'reservoir_pressure',
          'initialReservoirPressure',
          'initial_reservoir_pressure',
          'formationPressure',
          'formation_pressure',
          'reservoirPr',
          'pr',
        ],
        3,
      );
    }, [project]);

  useEffect(() => {
    if (
      reservoirPressureText === '' &&
      detectedReservoirPressure !==
        null
    ) {
      setReservoirPressureText(
        String(
          detectedReservoirPressure,
        ),
      );
    }
  }, [
    detectedReservoirPressure,
    reservoirPressureText,
  ]);

  const reservoirPressure =
    Number(
      reservoirPressureText,
    );

  const rawPoints =
    useMemo<
      RtaRawPoint[]
    >(() => {
      return productionRows
        .map(
          (row) => {
            const record =
              row as unknown as Record<
                string,
                unknown
              >;

            const pwf =
              getNumberByAliases(
                record,
                [
                  'bottomholePressure',
                  'bottomHolePressure',
                  'bottomhole_pressure',
                  'bottomholeFlowingPressure',
                  'bottom_hole_pressure',
                  'flowingBottomholePressure',
                  'pwf',
                  'bhp',
                ],
              );

            return {
              date:
                String(
                  record.date ??
                    '',
                ),

              q:
                Number(
                  record.gasRate ??
                    NaN,
                ),

              pwf:
                pwf ??
                NaN,
            };
          },
        )
        .filter(
          (point) =>
            point.date !== '' &&
            Number.isFinite(
              point.q,
            ) &&
            Number.isFinite(
              point.pwf,
            ),
        );
    }, [productionRows]);

  const rtaPoints =
    useMemo(
      () =>
        buildRtaPoints(
          rawPoints,
          reservoirPressure,
        ),
      [
        rawPoints,
        reservoirPressure,
      ],
    );

  const [
    fitStartDate,
    setFitStartDate,
  ] = useState('');

  const [
    fitEndDate,
    setFitEndDate,
  ] = useState('');

  useEffect(() => {
    if (
      rtaPoints.length === 0
    ) {
      setFitStartDate('');
      setFitEndDate('');
      return;
    }

    const hasStart =
      rtaPoints.some(
        (point) =>
          point.date ===
          fitStartDate,
      );

    const hasEnd =
      rtaPoints.some(
        (point) =>
          point.date ===
          fitEndDate,
      );

    if (!hasStart) {
      setFitStartDate(
        rtaPoints[0].date,
      );
    }

    if (!hasEnd) {
      setFitEndDate(
        rtaPoints[
          rtaPoints.length - 1
        ].date,
      );
    }
  }, [
    rtaPoints,
    fitStartDate,
    fitEndDate,
  ]);

  const fitPoints =
    useMemo(() => {
      if (
        rtaPoints.length === 0
      ) {
        return [];
      }

      let start =
        rtaPoints.findIndex(
          (point) =>
            point.date ===
            fitStartDate,
        );

      let end =
        rtaPoints.findIndex(
          (point) =>
            point.date ===
            fitEndDate,
        );

      if (start < 0) {
        start = 0;
      }

      if (end < 0) {
        end =
          rtaPoints.length -
          1;
      }

      if (start > end) {
        [
          start,
          end,
        ] = [
          end,
          start,
        ];
      }

      return rtaPoints.slice(
        start,
        end + 1,
      );
    }, [
      rtaPoints,
      fitStartDate,
      fitEndDate,
    ]);

  const sqrtTimeFit =
    useMemo(
      () =>
        fitRtaLinearTrend(
          fitPoints,
          'sqrtT',
        ),
      [fitPoints],
    );

  const sqrtMbTimeFit =
    useMemo(
      () =>
        fitRtaLinearTrend(
          fitPoints,
          'sqrtTmb',
        ),
      [fitPoints],
    );

  const [pseudoEngineering, setPseudoEngineering] = useState<{ coverageOk: boolean; fit: RtaLinearFit | null }>({ coverageOk: false, fit: null });

  const usePseudoPressureEngineering = pseudoEngineering.coverageOk && pseudoEngineering.fit !== null;

  const engineeringFit = usePseudoPressureEngineering ? pseudoEngineering.fit : sqrtTimeFit;

  const engineeringMethod = usePseudoPressureEngineering ? '实气拟压力 RTA' : '压力平方近似 RTA';
  
    if (!project) {
    return (
      <div
        style={{
          padding: 20,
          color:
            ETH.textMuted,
        }}
      >
        正在初始化 RTA 数据...
      </div>
    );
  }

  const lastPoint =
    rtaPoints.length > 0
      ? rtaPoints[
          rtaPoints.length - 1
        ]
      : null;

  const pwfs =
    rtaPoints.map(
      (point) =>
        point.pwf,
    );

  const minPwf =
    pwfs.length > 0
      ? Math.min(...pwfs)
      : NaN;

  const maxPwf =
    pwfs.length > 0
      ? Math.max(...pwfs)
      : NaN;

  return (
    <div>
      {/* 页面标题 */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
            }}
          >
            RTA 分析
          </h3>

          <div
            style={{
              marginTop: 5,
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            基于生产动态与井底流压开展压力归一化和流动阶段诊断
          </div>
        </div>

        <div
          style={{
            textAlign:
              'right',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            有效 RTA 点
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 20,
              fontWeight: 800,
              fontFamily:
                ETH.fontMono,
            }}
          >
            {
              rtaPoints.length
            }
          </div>
        </div>
      </div>

      {/* 基础设置 */}
      <div
        style={{
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 20,
            flexWrap: 'wrap',
            alignItems:
              'flex-end',
          }}
        >
          <label
            style={{
              display: 'grid',
              gap: 5,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color:
                  ETH.textSec,
              }}
            >
              地层压力 Pr
            </span>

            <div
              style={{
                display: 'flex',
                alignItems:
                  'center',
                gap: 6,
              }}
            >
              <input
                type="number"
                value={
                  reservoirPressureText
                }
                onChange={(
                  event,
                ) =>
                  setReservoirPressureText(
                    event.target
                      .value,
                  )
                }
                style={
                  inputStyle
                }
              />

              <span
                style={{
                  fontSize: 11,
                  color:
                    ETH.textMuted,
                }}
              >
                MPa
              </span>
            </div>
          </label>

          <label
            style={{
              display: 'grid',
              gap: 5,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color:
                  ETH.textSec,
              }}
            >
              拟合起点
            </span>

            <select
              value={
                fitStartDate
              }
              onChange={(
                event,
              ) =>
                setFitStartDate(
                  event.target
                    .value,
                )
              }
              style={
                selectStyle
              }
            >
              {rtaPoints.map(
                (
                  point,
                  index,
                ) => (
                  <option
                    key={`${point.date}-start-${index}`}
                    value={
                      point.date
                    }
                  >
                    {
                      point.date
                    }
                  </option>
                ),
              )}
            </select>
          </label>

          <label
            style={{
              display: 'grid',
              gap: 5,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color:
                  ETH.textSec,
              }}
            >
              拟合终点
            </span>

            <select
              value={
                fitEndDate
              }
              onChange={(
                event,
              ) =>
                setFitEndDate(
                  event.target
                    .value,
                )
              }
              style={
                selectStyle
              }
            >
              {rtaPoints.map(
                (
                  point,
                  index,
                ) => (
                  <option
                    key={`${point.date}-end-${index}`}
                    value={
                      point.date
                    }
                  >
                    {
                      point.date
                    }
                  </option>
                ),
              )}
            </select>
          </label>

          <div
            style={{
              marginLeft:
                'auto',
              fontSize: 11,
              color:
                ETH.textMuted,
              maxWidth: 400,
              lineHeight: 1.6,
            }}
          >
            <>
              当前工程解释：<b>{engineeringMethod}</b>。
              {usePseudoPressureEngineering
                ? ' PVT 已覆盖当前压力范围，优先采用实气拟压力 Δm/q 线性拟合。'
                : ' PVT 未完整覆盖或拟压力拟合不可用，当前自动回退压力平方近似。'}
            </>
          </div>
        </div>
      </div>

      {/* 数据摘要 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(0, 1fr))',
          gap: 12,
          marginTop: 14,
        }}
      >
        <StatusCard
          title="生产记录"
          value={`${productionRows.length} 条`}
          description="数据准备模块全部生产记录"
        />

        <StatusCard
          title="有效 RTA 点"
          value={`${rtaPoints.length} 条`}
          description="要求 q、Pwf、Pr 均有效"
        />

        <StatusCard
          title="井底流压范围"
          value={
            pwfs.length > 0
              ? `${fmt(
                  minPwf,
                  2,
                )}–${fmt(
                  maxPwf,
                  2,
                )} MPa`
              : '--'
          }
          description="用于检查生产压差变化"
        />

        <StatusCard
          title="累计产气"
          value={
            lastPoint
              ? `${fmt(
                  lastPoint.gp,
                  2,
                )} ×10⁴m³`
              : '--'
          }
          description="由日产气量梯形积分获得"
        />
      </div>

      {rtaPoints.length < 3 ? (
        <MissingDataPanel
          hasReservoirPressure={
            Number.isFinite(
              reservoirPressure,
            ) &&
            reservoirPressure >
              0
          }
          rawCount={
            rawPoints.length
          }
        />
      ) : (
        <>
          {/* RTA 拟合结果 */}
          <div
            style={{
              marginTop: 14,
              display: 'grid',
              gridTemplateColumns:
                '1fr 1fr',
              gap: 14,
            }}
          >
            <DiagnosticCard
              title="压力平方 √t 线性诊断"
              subtitle="Y = (Pr² − Pwf²) / q，X = √t"
              fit={
                sqrtTimeFit
              }
            />

            <DiagnosticCard
              title="压力平方 √tmb 归一化诊断"
              subtitle="Y = (Pr² − Pwf²) / q，X = √(Gp/q)"
              fit={
                sqrtMbTimeFit
              }
            />
          </div>

          {/* 图形 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              √t 流动诊断图
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color:
                  ETH.textMuted,
              }}
            >
              观察压力平方归一化响应在选定生产阶段内是否呈近似直线
            </div>

            <RtaChart
              points={
                rtaPoints
              }
              fitPoints={
                fitPoints
              }
              fit={
                sqrtTimeFit
              }
              xType="sqrtT"
              xLabel="√t / d¹ᐟ²"
            />
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              物质平衡时间诊断图
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color:
                  ETH.textMuted,
              }}
            >
              使用 tmb = Gp/q
              对变产量历史进行时间归一化辅助诊断
            </div>

            <RtaChart
              points={
                rtaPoints
              }
              fitPoints={
                fitPoints
              }
              fit={
                sqrtMbTimeFit
              }
              xType="sqrtTmb"
              xLabel="√tmb / d¹ᐟ²"
            />
          </div>

          <RtaPseudopressurePanel projectId={projectId} points={rtaPoints} fitPoints={fitPoints} onEngineeringFitChange={setPseudoEngineering} />

          <RtaEngineeringPanel project={project} fitPoints={fitPoints} fit={engineeringFit} methodLabel={engineeringMethod} />

          <SensitivityForecastPanel
            project={project}
            points={rtaPoints}
          />

          {/* 明细表 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              RTA 计算明细
            </div>

            <div
              style={{
                overflowX:
                  'auto',
              }}
            >
              <table
                style={{
                  width: '100%',
                  minWidth: 1000,
                  borderCollapse:
                    'collapse',
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>
                      日期
                    </th>

                    <th style={thStyle}>
                      t / d
                    </th>

                    <th style={thStyle}>
                      q / 10⁴m³/d
                    </th>

                    <th style={thStyle}>
                      Pwf / MPa
                    </th>

                    <th style={thStyle}>
                      Pr²−Pwf²
                    </th>

                    <th style={thStyle}>
                      (Pr²−Pwf²)/q
                    </th>

                    <th style={thStyle}>
                      Gp / 10⁴m³
                    </th>

                    <th style={thStyle}>
                      tmb / d
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rtaPoints.map(
                    (
                      point,
                      index,
                    ) => (
                      <tr
                        key={`${point.date}-${index}`}
                      >
                        <td
                          style={
                            tdStyle
                          }
                        >
                          {
                            point.date
                          }
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.t,
                            1,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.q,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.pwf,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.deltaP2,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.inverseNormalizedRate2,
                            4,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.gp,
                            2,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.tmb,
                            2,
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
              fontSize: 11,
              color:
                ETH.textMuted,
              lineHeight: 1.8,
            }}
          >
            <div>
              · 当前模块首先用于流动阶段诊断，
              暂不根据一条直线直接给出裂缝半长或渗透率。
            </div>

            <div>
              · R²
              用于描述当前选定数据段的直线一致程度，
              不能单独证明某一流动机理。
            </div>

            <div>
              · 若井底流压变化较明显，
              相比单独观察 q−√t，
              压力归一化结果更具有解释意义。
            </div>

            <div>
              · 当有效 PVT 覆盖当前 Pwf 至 Pr 压力范围且拟压力拟合有效时，工程解释优先采用实气拟压力；否则自动回退至压力平方近似。
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MissingDataPanel({
  hasReservoirPressure,
  rawCount,
}: {
  hasReservoirPressure: boolean;
  rawCount: number;
}) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: 28,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
        textAlign:
          'center',
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        RTA 数据尚不完整
      </div>

      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          lineHeight: 1.8,
          color:
            ETH.textMuted,
        }}
      >
        {!hasReservoirPressure && (
          <div>
            请填写地层压力
            Pr。
          </div>
        )}

        {rawCount < 3 && (
          <div>
            请在“数据准备 →
            生产动态数据”中至少录入3条
            日产气量和井底流压
            Pwf。
          </div>
        )}
      </div>
    </div>
  );
}

function DiagnosticCard({
  title,
  subtitle,
  fit,
}: {
  title: string;
  subtitle: string;
  fit:
    RtaLinearFit | null;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {subtitle}
      </div>

      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gap: 7,
        }}
      >
        <ResultLine
          label="拟合点数"
          value={
            fit
              ? `${fit.count}`
              : '--'
          }
        />

        <ResultLine
          label="斜率"
          value={
            fit
              ? fmt(
                  fit.slope,
                  6,
                )
              : '--'
          }
        />

        <ResultLine
          label="截距"
          value={
            fit
              ? fmt(
                  fit.intercept,
                  6,
                )
              : '--'
          }
        />

        <ResultLine
          label="R²"
          value={
            fit
              ? fmt(
                  fit.r2,
                  4,
                )
              : '--'
          }
          strong
        />

        <ResultLine
          label="RMSE"
          value={
            fit
              ? fmt(
                  fit.rmse,
                  4,
                )
              : '--'
          }
        />
      </div>
    </div>
  );
}

function RtaChart({
  points,
  fitPoints,
  fit,
  xType,
  xLabel,
}: {
  points: RtaPoint[];
  fitPoints: RtaPoint[];

  fit:
    RtaLinearFit | null;

  xType:
    | 'sqrtT'
    | 'sqrtTmb';

  xLabel: string;
}) {
  const width = 900;
  const height = 350;

  const left = 72;
  const right = 30;
  const top = 25;
  const bottom = 52;

  const plotW =
    width -
    left -
    right;

  const plotH =
    height -
    top -
    bottom;

  const xs =
    points.map(
      (point) =>
        point[xType],
    );

  const ys =
    points.map(
      (point) =>
        point.inverseNormalizedRate2,
    );

  const rawMaxX =
    Math.max(
      1,
      ...xs,
    );

  const rawMinY =
    Math.min(
      ...ys,
    );

  const rawMaxY =
    Math.max(
      ...ys,
    );

  const ySpan =
    Math.max(
      rawMaxY -
        rawMinY,
      1,
    );

  const minY =
    Math.max(
      0,
      rawMinY -
        ySpan *
          0.08,
    );

  const maxY =
    rawMaxY +
    ySpan *
      0.08;

  const maxX =
    rawMaxX *
    1.05;

  const x = (
    value: number,
  ) =>
    left +
    (
      value /
      maxX
    ) *
      plotW;

  const y = (
    value: number,
  ) =>
    top +
    (
      1 -
      (
        value -
        minY
      ) /
        (
          maxY -
          minY
        )
    ) *
      plotH;

  const selectedKeys =
    new Set(
      fitPoints.map(
        (point) =>
          `${point.date}|${point.t}`,
      ),
    );

  const fitPath =
    fit
      ? [
          {
            x:
              fit.minX,
            y:
              fit.intercept +
              fit.slope *
                fit.minX,
          },
          {
            x:
              fit.maxX,
            y:
              fit.intercept +
              fit.slope *
                fit.maxX,
          },
        ]
      : [];

  return (
    <div
      style={{
        marginTop: 12,
        overflowX:
          'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height,
          display: 'block',
        }}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            minY +
            (
              maxY -
              minY
            ) *
              index /
              5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={
                  left - 8
                }
                y={
                  y(value) +
                  4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            maxX *
            index /
            5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {fitPath.length ===
          2 && (
          <line
            x1={x(
              fitPath[0].x,
            )}
            y1={y(
              fitPath[0].y,
            )}
            x2={x(
              fitPath[1].x,
            )}
            y2={y(
              fitPath[1].y,
            )}
            stroke="#b64f35"
            strokeWidth={2}
          />
        )}

        {points.map(
          (
            point,
            index,
          ) => {
            const selected =
              selectedKeys.has(
                `${point.date}|${point.t}`,
              );

            return (
              <circle
                key={`${point.date}-${index}`}
                cx={x(
                  point[
                    xType
                  ],
                )}
                cy={y(
                  point.inverseNormalizedRate2,
                )}
                r={
                  selected
                    ? 4.2
                    : 3
                }
                fill={
                  selected
                    ? ETH.text
                    : '#aaa'
                }
              />
            );
          },
        )}

        <text
          x={
            left +
            plotW / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
        >
          {xLabel}
        </text>

        <text
          x={16}
          y={
            top +
            plotH / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
          transform={`rotate(-90 16 ${
            top +
            plotH / 2
          })`}
        >
          (Pr²−Pwf²)/q
        </text>
      </svg>
    </div>
  );
}

function StatusCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 18,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function ResultLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          'space-between',
        gap: 12,
        padding:
          strong
            ? '9px 0'
            : '5px 0',
        borderTop:
          strong
            ? `1px solid ${ETH.headerBorder}`
            : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontSize:
            strong
              ? 15
              : 11,
          fontWeight:
            strong
              ? 800
              : 600,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function getNumberByAliases(
  record: Record<
    string,
    unknown
  >,
  aliases: string[],
) {
  const normalizedAliases =
    aliases.map(
      normalizeKey,
    );

  for (
    const [
      key,
      value,
    ] of Object.entries(
      record,
    )
  ) {
    if (
      normalizedAliases.includes(
        normalizeKey(key),
      )
    ) {
      const number =
        Number(value);

      if (
        Number.isFinite(
          number,
        )
      ) {
        return number;
      }
    }
  }

  return null;
}

function findNumberRecursive(
  input: unknown,
  aliases: string[],
  maxDepth: number,
  depth = 0,
): number | null {
  if (
    input === null ||
    typeof input !==
      'object' ||
    depth >
      maxDepth
  ) {
    return null;
  }

  const record =
    input as Record<
      string,
      unknown
    >;

  const direct =
    getNumberByAliases(
      record,
      aliases,
    );

  if (direct !== null) {
    return direct;
  }

  for (
    const value of Object.values(
      record,
    )
  ) {
    if (
      value !== null &&
      typeof value ===
        'object'
    ) {
      const nested =
        findNumberRecursive(
          value,
          aliases,
          maxDepth,
          depth + 1,
        );

      if (
        nested !== null
      ) {
        return nested;
      }
    }
  }

  return null;
}

function normalizeKey(
  value: string,
) {
  return value
    .replace(
      /[^a-zA-Z0-9]/g,
      '',
    )
    .toLowerCase();
}

function fmt(
  value: number,
  digits = 4,
) {
  if (
    !Number.isFinite(
      value,
    )
  ) {
    return '--';
  }

  if (
    Math.abs(value) >
      1e6 ||
    (
      Math.abs(value) >
        0 &&
      Math.abs(value) <
        1e-5
    )
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}

const inputStyle:
  React.CSSProperties = {
    width: 130,
    padding: '6px 8px',
    boxSizing:
      'border-box',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };

const selectStyle:
  React.CSSProperties = {
    minWidth: 150,
    padding: '6px 8px',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };

const thStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
    textAlign:
      'center',
    color:
      ETH.textSec,
    whiteSpace:
      'nowrap',
  };

const tdStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
  };

const numberTdStyle:
  React.CSSProperties = {
    ...tdStyle,
    textAlign:
      'right',
    fontFamily:
      ETH.fontMono,
  };